import * as THREE from 'three';
import { ActorView } from '../render/ActorView';
import type { AssetLibrary } from '../render/assets';
import { Lighting, lookForZone } from '../render/Lighting';
import { weaponFor } from '../render/ZoneVisuals';
import { Actor } from '../sim/actor';
import { registry } from '../sim/registry';

/**
 * The 3D stage behind the menus: a lit stone dais in a dusky courtyard with
 * the chosen class idling on it, turning slowly and now and then showing a
 * skill (Lost Ark presents its classes the same way on character creation).
 */
export class Showcase {
  readonly scene = new THREE.Scene();
  private readonly lighting: Lighting;
  private view: ActorView | null = null;
  private actor: Actor;
  private classId: string | null = null;
  private loading: string | null = null;
  private time = 0;
  private nextMove = 5;
  private readonly focus = new THREE.Vector3(0, 0, 0);
  private embers: THREE.Points;

  constructor(private assets: AssetLibrary) {
    this.lighting = new Lighting(this.scene);
    const look = { ...lookForZone({ id: 'havenreach', biome: 'town' }), fogNear: 18, fogFar: 60, background: 0x0d0f14, fogColor: 0x0d0f14 };
    this.lighting.applyBiome(look);
    this.actor = new Actor({ id: -7, kind: 'player', owner: 'menu', team: 'players', name: '', pos: { x: 0, z: 0 } });
    this.actor.facing = 0;

    // Dais: a stepped stone disc with a warm rune ring, on dark flagstones.
    const stone = new THREE.MeshStandardMaterial({ color: 0x6f6a62, roughness: 0.85, metalness: 0.05 });
    const top = new THREE.Mesh(new THREE.CylinderGeometry(2.1, 2.25, 0.3, 48), stone);
    top.position.y = -0.15;
    top.receiveShadow = true;
    const step = new THREE.Mesh(new THREE.CylinderGeometry(2.9, 3.1, 0.25, 48), stone);
    step.position.y = -0.42;
    step.receiveShadow = true;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1.75, 1.85, 64),
      new THREE.MeshBasicMaterial({ color: 0xffb04a, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.01;
    const floor = new THREE.Mesh(new THREE.CircleGeometry(40, 64), new THREE.MeshStandardMaterial({ color: 0x23211f, roughness: 1 }));
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.55;
    floor.receiveShadow = true;
    this.scene.add(top, step, ring, floor);

    // Braziers either side for warm key light (the prop model once it loads).
    for (const x of [-3.6, 3.6]) {
      const light = new THREE.PointLight(0xff9a40, 18, 12, 1.6);
      light.position.set(x, 1.6, 1.2);
      this.scene.add(light);
      void this.assets.load('brazier').then((t) => {
        if (t.placeholder) return;
        const b = this.assets.instantiate(t);
        b.position.set(x, -0.55, 1.2);
        b.scale.setScalar(1.2);
        this.scene.add(b);
      });
    }

    // Drifting embers.
    const n = 160;
    const pos = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 14;
      pos[i * 3 + 1] = Math.random() * 6;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 8;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.embers = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffa050, size: 0.06, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.scene.add(this.embers);
  }

  /** Shows a class on the dais (null clears it). */
  setClass(classId: string | null): void {
    if (classId === this.classId || classId === this.loading) return;
    if (!classId || !registry.classes.has(classId)) {
      this.clearView();
      this.classId = null;
      return;
    }
    const cls = registry.cls(classId);
    this.loading = classId;
    this.actor.look = { model: cls.model, weapon: cls.weapon, tint: cls.hue };
    void this.assets.load(cls.model).then((t) => {
      if (this.loading !== classId) return;
      this.loading = null;
      this.clearView();
      const v = new ActorView(this.actor, t, this.assets, cls.model);
      v.setWeapon(weaponFor(this.actor));
      v.setCastShadow(true);
      this.view = v;
      this.classId = classId;
      this.scene.add(v.root);
      this.nextMove = 2.5;
      v.play('Victory', 0.1, undefined, false);
    });
  }

  private clearView(): void {
    if (!this.view) return;
    this.scene.remove(this.view.root);
    this.view.dispose();
    this.view = null;
  }

  /** Positions the camera and animates the stage. `offsetX` shifts the hero to one side of the screen. */
  update(dt: number, camera: THREE.PerspectiveCamera, offsetX = 0, title = false): void {
    this.time += dt;
    const a = this.actor;
    a.facing = Math.sin(this.time * 0.25) * 0.45;
    this.nextMove -= dt;
    if (this.view && this.nextMove <= 0) {
      this.nextMove = 6 + Math.random() * 4;
      const moves = ['Attack1', 'Attack2', 'Attack3', 'Spin', 'Slam', 'Cast', 'Buff', 'Leap'];
      this.view.play(moves[Math.floor(Math.random() * moves.length)] ?? 'Attack1', 0.12, undefined, false);
    }
    this.view?.update(dt, a, 1, () => 0);
    const p = this.embers.geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      let y = p.getY(i) + dt * (0.25 + (i % 5) * 0.05);
      if (y > 6) y = 0;
      p.setY(i, y);
    }
    p.needsUpdate = true;
    // A slow dolly around the dais. Menus frame the hero's chest; the title
    // looks higher so the hero stands in the lower third under the logo.
    const ang = Math.sin(this.time * 0.12) * 0.18;
    const dist = title ? 9.5 : 9;
    camera.fov = 32;
    // Moving the camera right by `offsetX` puts the hero left of centre.
    camera.position.set(Math.sin(ang) * dist + offsetX, title ? 2.2 : 2.3, Math.cos(ang) * dist);
    this.focus.set(offsetX, title ? 2.6 : 1.2, 0);
    camera.lookAt(this.focus);
    camera.updateProjectionMatrix();
    this.lighting.follow(this.focus);
  }
}
